// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { TrouterSubscriptionState } from "@ms/planner-models/lib/local/client/trouter/TrouterSubscriptionState";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Slice } from "../../slice/Slice";
import { Store } from "../Store";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { AbsentStoreEntityStatus } from "../../utilities/StatusUtilities";
/**
 * Store for managing Trouter state data
 */ export class TrouterStore extends EventPublishingStore {
    get name() {
        return Store.Trouter;
    }
    getSubscription(viewUpdateKey) {
        const result = this.subscriptionSlice.get(viewUpdateKey);
        if (!result.data) {
            return {
                data: null,
                status: AbsentStoreEntityStatus
            };
        }
        return result;
    }
    /**
     * Get the subscription status for a query type and scope entity
     */ getSubscriptionStatus(queryType, scopeEntityId) {
        const compositeKey = this.createCompositeKey(queryType, scopeEntityId);
        return this.subscriptionStatuses.get(compositeKey) ?? null;
    }
    /**
     * Get the Trouter endpoint ID for subscriptions.
     * The endpoint ID is derived from the app session ID (available immediately at startup).
     */ getTrouterEndpointId() {
        // Check if Trouter is enabled and get endpoint ID
        const endpointId = this.computeTrouterEndpointId();
        if (!endpointId) {
            return {
                data: null,
                status: {
                    isFetching: false,
                    state: StoreEntityState.UnableToFetch,
                    timestamp: null
                }
            };
        }
        return {
            data: endpointId,
            status: {
                isFetching: false,
                state: StoreEntityState.Present,
                timestamp: null
            }
        };
    }
    /**
     * Get the current Trouter connection state.
     */ getConnectionState() {
        return {
            ...this.connectionState
        };
    }
    /**
     * Get all failed subscriptions that are past their retry-after period and should be retried.
     */ getFailedSubscriptionsToRetry() {
        const now = Temporal.Now.instant();
        const result = [];
        for (const [compositeKey, status] of this.subscriptionStatuses){
            // Only consider failed subscriptions that should be retried
            if (status.state !== TrouterSubscriptionState.Failed || !status.shouldRetry) {
                continue;
            }
            // Check if retry-after period has elapsed
            if (status.retryAfterSeconds != null) {
                const retryAfterDuration = Temporal.Duration.from({
                    seconds: status.retryAfterSeconds
                });
                const retryAfterExpiry = status.timestamp.add(retryAfterDuration);
                if (Temporal.Instant.compare(now, retryAfterExpiry) < 0) {
                    continue;
                }
            }
            const parsed = this.parseCompositeKey(compositeKey);
            if (!parsed) {
                continue;
            }
            result.push({
                scopeEntityId: parsed.scopeEntityId,
                queryType: parsed.queryType,
                status
            });
        }
        return result;
    }
    /**
     * Check if a subscription is fresh and does not need renewal.
     * A subscription is fresh if it is in Subscribed state and was created within
     * the refresh threshold period: max(TTL - 2×deltaSyncInterval, TTL/2).
     */ isSubscriptionFresh(queryType, scopeEntityId) {
        const status = this.getSubscriptionStatus(queryType, scopeEntityId);
        if (!status || status.state !== TrouterSubscriptionState.Subscribed) {
            return false;
        }
        const thresholdMs = this.getRefreshThresholdMs();
        const refreshAt = status.timestamp.add(Temporal.Duration.from({
            milliseconds: thresholdMs
        }));
        return Temporal.Instant.compare(Temporal.Now.instant(), refreshAt) < 0;
    }
    /**
     * Get all active subscriptions that are nearing expiry.
     * Uses the refresh threshold: max(TTL - 2×deltaSyncInterval, TTL/2).
     */ getSubscriptionsNearingExpiry() {
        const thresholdMs = this.getRefreshThresholdMs();
        const expiryThreshold = Temporal.Now.instant().subtract(Temporal.Duration.from({
            milliseconds: thresholdMs
        }));
        const result = [];
        for (const [compositeKey, status] of this.subscriptionStatuses){
            // Only consider active (Subscribed) subscriptions
            if (status.state !== TrouterSubscriptionState.Subscribed) {
                continue;
            }
            // Check if the subscription is nearing expiry
            if (Temporal.Instant.compare(status.timestamp, expiryThreshold) < 0) {
                const parsed = this.parseCompositeKey(compositeKey);
                if (!parsed) {
                    continue;
                }
                result.push({
                    scopeEntityId: parsed.scopeEntityId,
                    queryType: parsed.queryType,
                    timestamp: status.timestamp
                });
            }
        }
        return result;
    }
    /**
     * Get all subscription statuses for display in devtools.
     */ getAllSubscriptionStatuses() {
        const result = [];
        for (const [compositeKey, status] of this.subscriptionStatuses){
            const parsed = this.parseCompositeKey(compositeKey);
            if (!parsed) {
                continue;
            }
            result.push({
                scopeEntityId: parsed.scopeEntityId,
                queryType: parsed.queryType,
                state: status.state,
                timestamp: status.timestamp,
                shouldRetry: status.shouldRetry,
                retryAfterSeconds: status.retryAfterSeconds,
                lastNotificationAt: status.lastNotificationAt
            });
        }
        return result;
    }
    /**
     * Calculates the refresh threshold in milliseconds: max(TTL - 2×deltaSyncInterval, TTL/2).
     * Subscriptions older than this threshold should be renewed.
     */ getRefreshThresholdMs() {
        const { subscriptionTtlMs, trouterModeDeltaSyncIntervalInSeconds } = this.configProvider().settings.trouterSettings;
        const bufferMs = 2 * trouterModeDeltaSyncIntervalInSeconds * 1000;
        const minimumThresholdMs = Math.floor(subscriptionTtlMs / 2);
        return Math.max(subscriptionTtlMs - bufferMs, minimumThresholdMs);
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.TrouterRegistrationSucceededAction:
                storeChanged = this.handleRegistrationSucceeded(action);
                break;
            case ActionType.TrouterDisconnectedAction:
                storeChanged = this.handleDisconnected(action);
                break;
            case ActionType.TrouterSubscriptionSuccessAction:
                storeChanged = this.handleSubscriptionSuccess(action);
                break;
            case ActionType.TrouterSubscriptionFailedAction:
                storeChanged = this.handleSubscriptionFailed(action);
                break;
            case ActionType.TrouterNotificationReceivedAction:
                storeChanged = this.handleNotificationReceived(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleRegistrationSucceeded(action) {
        const newState = {
            isConnected: true,
            endpointUrl: action.endpointUrl,
            connectionInfo: action.connectionInfo,
            connectedAt: action.connectedAt
        };
        // Check if connection state changed
        const stateChanged = this.connectionState.isConnected !== newState.isConnected || this.connectionState.endpointUrl !== newState.endpointUrl || this.connectionState.connectionInfo?.connectionId !== newState.connectionInfo?.connectionId;
        if (stateChanged) {
            this.connectionState = newState;
        }
        return stateChanged;
    }
    handleDisconnected(_action) {
        // Only update if we were previously connected
        if (this.connectionState.isConnected) {
            this.connectionState = {
                isConnected: false
            };
            return true;
        }
        return false;
    }
    handleSubscriptionSuccess(action) {
        const compositeKey = this.createCompositeKey(action.queryType, action.scopeEntityId);
        const existing = this.subscriptionStatuses.get(compositeKey);
        // Update status to Subscribed, preserving lastNotificationAt from any prior subscription
        this.subscriptionStatuses.set(compositeKey, {
            state: TrouterSubscriptionState.Subscribed,
            timestamp: Temporal.Now.instant(),
            lastNotificationAt: existing?.lastNotificationAt
        });
        const subscription = {
            scopeEntityId: action.scopeEntityId,
            queryType: action.queryType,
            timestamp: Temporal.Now.instant()
        };
        this.subscriptionSlice.handleFetchSucceeded(action.viewUpdateKey, subscription);
        return true;
    }
    handleSubscriptionFailed(action) {
        const compositeKey = this.createCompositeKey(action.queryType, action.scopeEntityId);
        this.subscriptionStatuses.set(compositeKey, {
            state: TrouterSubscriptionState.Failed,
            timestamp: Temporal.Now.instant(),
            shouldRetry: action.shouldRetry,
            retryAfterSeconds: action.retryAfterSeconds
        });
        return true;
    }
    handleNotificationReceived(action) {
        const compositeKey = this.createCompositeKey(action.queryType, action.scopeEntityId);
        const existing = this.subscriptionStatuses.get(compositeKey);
        // Only update if the subscription exists
        if (!existing) {
            return false;
        }
        this.subscriptionStatuses.set(compositeKey, {
            ...existing,
            lastNotificationAt: action.receivedAt
        });
        return true;
    }
    /**
     * Creates a composite key from query type and scope entity ID.
     * Used for indexing subscription statuses.
     */ createCompositeKey(queryType, scopeEntityId) {
        return `${queryType}|${scopeEntityId}`;
    }
    /**
     * Parses a composite key back into its query type and scope entity ID components.
     * Returns null if the key format is invalid.
     */ parseCompositeKey(compositeKey) {
        const separatorIndex = compositeKey.indexOf("|");
        /* istanbul ignore next: defensive check - keys are always created with separator */ if (separatorIndex === -1) {
            return null;
        }
        return {
            queryType: compositeKey.substring(0, separatorIndex),
            scopeEntityId: compositeKey.substring(separatorIndex + 1)
        };
    }
    /**
     * Gets the Trouter endpoint ID if Trouter is enabled.
     * Returns null if Trouter is disabled via flight or settings.
     */ computeTrouterEndpointId() {
        const config = this.configProvider();
        // Check if Trouter is enabled via flight
        if (!config.flights.EnableTrouterIntegration) {
            return null;
        }
        // Check if Trouter is disabled via settings
        if (config.settings.trouterSettings?.disable) {
            return null;
        }
        // Return session ID as the endpoint ID
        return config.sessionMetaData.sessionId || null;
    }
    constructor(loggers, configProvider){
        super(), this.loggers = loggers, this.configProvider = configProvider, /** Connection state (tracks isConnected, endpointUrl, connectionInfo) */ this.connectionState = {
            isConnected: false
        }, /** Map of subscription keys (relationship|parentEntityId) to status */ this.subscriptionStatuses = new Map(), /** Slice for storing subscriptions indexed by viewUpdateKey */ this.subscriptionSlice = new Slice(TableName.TrouterSubscription, {
            /** Index for serving data queries for subscriptions by query type and scope entity */ queryTypeAndScope: (subscription)=>`${subscription.queryType}|${subscription.scopeEntityId}`
        }, this.configProvider);
    }
}
